/**
 * Contratos declarativos das visões de fila. Genéricos de propósito: nenhum
 * arquivo desta pasta pode conhecer um domínio (pagamento, plantão, médico).
 */

export type QueueViewMode = 'list' | 'table' | 'kanban'

export type QueueTone = 'neutral' | 'info' | 'ok' | 'warn' | 'crit'

export interface QueueViewOption {
  value: QueueViewMode
  label: string
}

export const QUEUE_VIEW_OPTIONS: QueueViewOption[] = [
  { value: 'list', label: 'Lista' },
  { value: 'table', label: 'Tabela' },
  { value: 'kanban', label: 'Kanban' }
]

export interface QueueColumn<T> {
  key: string
  label: string
  /** Texto simples da célula. O slot `cell-<key>` tem prioridade sobre ele. */
  value?: (row: T) => string
  sortable?: boolean
  align?: 'left' | 'right'
  /** Classe Tailwind de largura, ex.: `'w-24'`. */
  widthClass?: string
}

export interface QueueSort {
  key: string
  direction: 'asc' | 'desc'
}

export interface QueueGroup<T> {
  key: string
  title: string
  tone?: QueueTone
  rows: T[]
  /** Seção aberta na primeira renderização. Padrão: `false`. */
  defaultOpen?: boolean
  /** Texto curto ao lado da contagem (ex.: o que o grupo significa). */
  hint?: string
}

export interface QueueKanbanColumn<T> {
  key: string
  title: string
  tone?: QueueTone
  matches: (row: T) => boolean
  /** Texto curto embaixo do título da coluna. */
  hint?: string
}

export interface QueueBulkAction {
  key: string
  label: string
}

/** Selo (pill) por tom — paleta Tailwind do painel. */
export const QUEUE_TONE_PILL_CLASS: Record<QueueTone, string> = {
  neutral: 'bg-lightGray30 text-neutral50',
  info: 'bg-blue-50 text-blue-700',
  ok: 'bg-green-50 text-green-700',
  warn: 'bg-orange-50 text-orange-700',
  crit: 'bg-red-50 text-red-700'
}

/** Faixa superior da coluna do Kanban, por tom. */
export const QUEUE_TONE_BORDER_CLASS: Record<QueueTone, string> = {
  neutral: 'border-t-lightGray30',
  info: 'border-t-blue-500',
  ok: 'border-t-green-600',
  warn: 'border-t-orange-500',
  crit: 'border-t-red-600'
}

/* ------------------------------------------------------------------------- *
 * Contrato de Visualizações do Módulo (spec 2026-09-25 §5). Espelho do
 * `src/common/module-views/module-view.types.ts` do backend. Genérico: nenhum
 * domínio aqui — o payload específico de cada módulo entra por `TPayload`.
 * ------------------------------------------------------------------------- */

export type ModuleViewKey = 'ACTIVITY_REQUESTS' | 'PRIORITY_PAYMENTS'

export type ModuleViewSlaState =
  | 'NOT_STARTED'
  | 'ON_TIME'
  | 'ATTENTION'
  | 'URGENT'
  | 'OVERDUE'
  | 'DONE'

export interface ModuleViewSla {
  state: ModuleViewSlaState
  deadlineAt: string | null
  startedAt: string | null
  completedAt: string | null
  /** Em `serverNow`; negativo = estourado. */
  remainingSeconds: number | null
  phase: string | null
}

export interface ModuleViewStage {
  key: string
  label: string
  tone: QueueTone
  order: number
  terminal: boolean
  hint?: string
}

export interface ModuleViewBadge {
  key: string
  label: string
  tone: QueueTone
}

export interface ModuleViewActor {
  doctorId: number
  name: string
  crm: string | null
  ourTeam: boolean
}

export interface ModuleViewLocation {
  companyId: number | null
  healthUnitId: number
  healthUnitName: string
  scheduleId: number
  scheduleName: string
  shiftName: string | null
}

export interface ModuleViewItem<TPayload> {
  module: ModuleViewKey
  /** `${module}:${entityId}` — único entre módulos. */
  id: string
  entityId: number
  stage: string
  sla: ModuleViewSla | null
  title: string
  subtitle: string | null
  occurredAt: string
  /** Offset do item (`-03:00`). */
  timezone: string
  actor: ModuleViewActor | null
  location: ModuleViewLocation
  badges: ModuleViewBadge[]
  value: { amount: number; currency: 'BRL' } | null
  detailRef: { module: ModuleViewKey; entityId: number }
  payload: TPayload
}

export interface ModuleViewListResponse<TPayload> {
  module: ModuleViewKey
  serverNow: string
  stages: ModuleViewStage[]
  items: Array<ModuleViewItem<TPayload>>
  count: number
  page: number
  limit: number
}

export interface ModuleViewCounters {
  module: ModuleViewKey
  serverNow: string
  byStage: Record<string, number>
  byView: Record<string, number>
}
